import React from 'react';
import {
  Car, CheckCheck, CheckCircle2, ChevronDown, ChevronUp, Circle, CircleCheck, Eye, Package, Phone, RefreshCw, X,
} from 'lucide-react';

import { formatIST, itemsLabel } from '@/lib/utils';
import { getProductTypeName } from '@/lib/constants';
import DispatchLoadSheet from '@/components/phone/DispatchLoadSheet';
import {
  EmptyState, IconButton, PageHeader, PhonePage, ScreenLoader, SearchBar, Spinner, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, pcs } from '@/components/phone/theme';

/**
 * Loading a van, from the office's phone.
 *
 * The desktop builds the load in a rail beside the list; a phone has no rail,
 * so the list is the picker — tick the orders going, type a smaller number on
 * a line when only part of it fits, and a bar at the thumb says what is on the
 * van. Vehicle and driver are asked on the sheet that bar opens, the same sheet
 * the supervisor's phone dispatch uses. A trip on the road is a card that opens
 * to its orders, each with its own Delivered button and one for the whole van.
 */

const money = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : null);

function ReadyCard({ order, picked, going, partQty, showValue, onToggle, onQty, onSendAll, onOpen }) {
  const staying = (order.ready_quantity || 0) - going;
  const Icon = picked ? CircleCheck : Circle;
  return (
    <li
      data-testid={`ready-card-${order.id}`}
      className="mt-3 rounded-[10px] border px-3 py-3"
      style={{
        backgroundColor: picked ? Colors.accent.wash : Colors.background.primary,
        borderColor: picked ? Colors.accent.DEFAULT : Colors.border.default,
      }}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={picked}
          aria-label={`Load ${order.customer_name}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center"
        >
          <Icon className="h-6 w-6" style={{ color: picked ? Colors.accent.DEFAULT : Colors.text.disabled }} />
        </button>
        <button type="button" onClick={onToggle} className="min-w-0 flex-1 pt-2 text-left">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {itemsLabel(order.ready_items, order.customer_name)}
          </span>
          <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {pcs(order.ready_quantity)} · {order.order_id}
            {order.expected_delivery_date ? ` · due ${formatIST(order.expected_delivery_date, { dateOnly: true })}` : ''}
            {showValue && money(order.order_value) ? ` · ${money(order.order_value)}` : ''}
          </span>
        </button>
        <button
          type="button"
          onClick={onOpen}
          aria-label="Open order"
          className="flex h-11 w-11 shrink-0 items-center justify-center"
        >
          <Eye className="h-[18px] w-[18px]" style={{ color: Colors.accent.DEFAULT }} />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 pl-1">
        {!order.fully_ready && (
          <StatusPill
            label={order.total_item_count > 1
              ? `${order.ready_item_count} of ${order.total_item_count} items ready`
              : `${pcs(order.ready_quantity)} of ${pcs(order.total_quantity)}`}
            tone="caution"
          />
        )}
        {order.sent_quantity > 0 && <StatusPill label={`${pcs(order.sent_quantity)} already sent`} tone="neutral" />}
        {order.priority === 'high' && <StatusPill label="Urgent" tone="critical" />}
      </div>

      {/* the pieces going, asked for only once the order is on the van */}
      {picked && (
        <div className="mt-3 border-t pt-2" style={{ borderColor: Colors.border.subtle }} data-testid={`load-lines-${order.id}`}>
          {(order.ready_items || []).map((item) => {
            const ready = item.ready_quantity ?? item.quantity ?? 0;
            const typed = partQty[item.id];
            return (
              <div key={item.id} className="flex items-center gap-3 py-1.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium" style={{ color: Colors.text.primary }}>
                    {item.item_name || getProductTypeName(item.order_type)}
                  </p>
                  <p className="truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {ready === 0 ? 'No piece count on this item — it goes as it is.' : `of ${pcs(ready)} ready`}
                  </p>
                </div>
                {ready > 0 && (
                  <input
                    data-testid={`load-qty-${item.id}`}
                    type="number"
                    min="0"
                    max={ready}
                    className="h-11 w-24 rounded-[10px] border px-2 text-right text-[13px] font-semibold outline-none"
                    style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.text.primary }}
                    value={typed === undefined ? String(ready) : typed}
                    onChange={(e) => onQty(item, e.target.value)}
                    onFocus={(e) => e.target.select()}
                    inputMode="numeric"
                    aria-label={`Pieces of ${item.item_name || 'this item'} going out`}
                  />
                )}
              </div>
            );
          })}
          {staying > 0 && (
            <div className="mt-1 flex items-baseline justify-between gap-3">
              <p className="text-[11px]" style={{ color: Colors.semantic.caution }}>
                Sending {pcs(going)} — {pcs(staying)} stay here for the next trip.
              </p>
              <button type="button" onClick={onSendAll} className="shrink-0 text-[11px] font-bold" style={{ color: Colors.accent.DEFAULT }}>
                Send all
              </button>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function TripCard({ trip, open, onToggle, delivering, onDeliver, onOpenOrder }) {
  const orders = trip.orders || [];
  const pending = orders.filter((o) => o.delivery_status !== 'delivered');
  const done = orders.length - pending.length;
  return (
    <li
      data-testid={`trip-card-${trip.id}`}
      className="mt-3 rounded-[10px] border px-4 py-3"
      style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={open ? 'Collapse orders' : 'Expand orders'}
        data-testid={`trip-toggle-${trip.id}`}
        className="flex w-full items-center gap-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[15px] font-bold" style={{ color: Colors.text.primary }}>{trip.dispatch_number}</span>
          <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            {trip.vehicle_number}{trip.is_external_vehicle ? ' (hired)' : ''} · {trip.driver_name}{trip.is_external_driver ? ' (hired)' : ''}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {done} of {orders.length} delivered
            {trip.dispatch_date ? ` · left ${formatIST(trip.dispatch_date, { compact: true })}` : ''}
          </span>
        </span>
        <StatusPill label={pending.length === 0 ? 'Completed' : 'On the road'} tone={pending.length === 0 ? 'positive' : 'accent'} />
        {open
          ? <ChevronUp className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />
          : <ChevronDown className="h-[18px] w-[18px] shrink-0" style={{ color: Colors.text.tertiary }} />}
      </button>

      {open && (
        <div className="mt-3 border-t pt-2" style={{ borderColor: Colors.border.subtle }}>
          {!!trip.driver_mobile && (
            <a href={`tel:${trip.driver_mobile}`} className="flex items-center gap-1.5 py-1.5 text-[12px] font-semibold" style={{ color: Colors.status.success }}>
              <Phone className="h-3.5 w-3.5" /> {trip.driver_mobile}
            </a>
          )}
          {orders.map((order) => {
            const isDone = order.delivery_status === 'delivered';
            const busy = delivering === `${trip.id}:${order.id}`;
            const at = order.dispatched_at || trip.dispatch_date;
            return (
              <div key={order.id} className="flex items-center gap-2 py-2">
                <button type="button" onClick={() => onOpenOrder(order)} className="min-w-0 flex-1 text-left">
                  <span className="block truncate text-[13px] font-semibold" style={{ color: Colors.text.primary }}>
                    {itemsLabel((order.item_names || []).map((name) => ({ item_name: name })), order.customer_name)}
                  </span>
                  <span className="block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {order.customer_name}{order.firm_name ? ` — ${order.firm_name}` : ''} · {order.order_id}
                    {order.dispatched_quantity ? ` · ${pcs(order.dispatched_quantity)} sent` : ''}
                    {at ? ` · ${formatIST(at, { compact: true })}` : ''}
                  </span>
                  {order.is_part_load && (
                    <span className="block truncate text-[11px] font-medium" style={{ color: Colors.semantic.caution }}>
                      Part load · {pcs(order.pending_quantity)} still to send
                    </span>
                  )}
                  {isDone && !!order.delivered_at && (
                    <span className="block truncate text-[11px]" style={{ color: Colors.status.success }}>
                      Delivered {formatIST(order.delivered_at, { compact: true })}
                    </span>
                  )}
                </button>
                {isDone ? (
                  <CheckCheck className="h-5 w-5 shrink-0" style={{ color: Colors.status.success }} />
                ) : (
                  <button
                    type="button"
                    data-testid={`deliver-${trip.id}-${order.id}`}
                    onClick={() => onDeliver(trip.id, [order.id])}
                    disabled={!!delivering}
                    className="flex h-11 min-w-[84px] shrink-0 items-center justify-center rounded-[10px] px-3 text-[12px] font-bold disabled:opacity-70"
                    style={{ backgroundColor: Colors.status.success, color: Colors.text.inverse }}
                  >
                    {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : 'Delivered'}
                  </button>
                )}
              </div>
            );
          })}
          {pending.length > 1 && (
            <button
              type="button"
              data-testid={`deliver-trip-${trip.id}`}
              onClick={() => onDeliver(trip.id, pending.map((o) => o.id))}
              disabled={!!delivering}
              className="mt-2 flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] border text-[13px] font-semibold disabled:opacity-60"
              style={{ borderColor: Colors.semantic.positive, color: Colors.semantic.positive }}
            >
              <CheckCircle2 className="h-4 w-4" /> Whole van delivered
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export default function PhoneDispatch({
  view, onView, stats, loading, onSearch, onRefresh,
  readyOrders, picked, onTogglePick, orderGoing, partQty, onGoingQty, onSendAll, showValue, onOpenOrder,
  pickedQuantity, onClearLoad, sheetOpen, onOpenSheet, onCloseSheet,
  vehicles, drivers, submitting, onBook, onAddVehicle, onAddDriver,
  trips, expanded, onToggleTrip, delivering, onDeliver,
}) {
  const tabs = [
    { key: 'ready', label: `Ready ${stats.ready_for_dispatch || 0}`, testID: 'view-ready' },
    { key: 'active', label: `On the road ${stats.active_dispatches || 0}`, testID: 'view-active' },
    { key: 'history', label: `Done ${stats.completed_dispatches || 0}`, testID: 'view-history' },
  ];
  const summary = `${picked.length} order${picked.length === 1 ? '' : 's'} · ${pcs(pickedQuantity)}`;

  return (
    <PhonePage testID="dispatch-page">
      <PageHeader
        title="Dispatch"
        subtitle={picked.length ? `${summary} on the load` : `${stats.dispatched_orders || 0} in transit`}
        action={<IconButton icon={RefreshCw} onClick={onRefresh} label="Refresh" />}
      />

      <div className="px-5">
        <SearchBar placeholder="Order ID, voice number, customer…" onChange={onSearch} testID="dispatch-search-input" />
        <div className="mt-3">
          <TabRow tabs={tabs} active={view} onSelect={onView} />
        </div>
      </div>

      <div className={`flex-1 px-5 ${view === 'ready' && picked.length ? 'pb-28' : 'pb-8'}`}>
        {loading ? (
          <ScreenLoader />
        ) : view === 'ready' ? (
          readyOrders.length === 0
            ? <EmptyState icon={Package} message="No orders ready for dispatch" />
            : (
              <ul data-testid="ready-list">
                {readyOrders.map((order) => (
                  <ReadyCard
                    key={order.id}
                    order={order}
                    picked={picked.includes(order.id)}
                    going={orderGoing(order)}
                    partQty={partQty[order.id] || {}}
                    showValue={showValue}
                    onToggle={() => onTogglePick(order.id)}
                    onQty={(item, value) => onGoingQty(order, item, value)}
                    onSendAll={() => onSendAll(order)}
                    onOpen={() => onOpenOrder(order)}
                  />
                ))}
              </ul>
            )
        ) : (
          trips.length === 0
            ? <EmptyState icon={Car} message={view === 'history' ? 'No dispatch history yet' : 'Nothing on the road'} />
            : (
              <ul data-testid="trip-list">
                {trips.map((trip) => (
                  <TripCard
                    key={trip.id}
                    trip={trip}
                    open={expanded.has(trip.id)}
                    onToggle={() => onToggleTrip(trip.id)}
                    delivering={delivering}
                    onDeliver={onDeliver}
                    onOpenOrder={onOpenOrder}
                  />
                ))}
              </ul>
            )
        )}
      </div>

      {/* the load, at the thumb — it only exists once something is on it */}
      {view === 'ready' && picked.length > 0 && (
        <div
          data-testid="load-bar"
          className="fixed bottom-4 left-1/2 z-30 flex w-[calc(100%-2.5rem)] max-w-[23.5rem] -translate-x-1/2 items-center gap-3 rounded-[14px] px-3 py-2.5 shadow-lg"
          style={{ backgroundColor: Colors.neutral[800] }}
        >
          <button
            type="button"
            onClick={onClearLoad}
            aria-label="Clear the load"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10"
          >
            <X className="h-[18px] w-[18px]" style={{ color: Colors.text.inverse }} />
          </button>
          <p className="min-w-0 flex-1 truncate text-[12px] font-semibold" style={{ color: Colors.text.inverse }}>{summary}</p>
          <button
            type="button"
            data-testid="open-load-sheet"
            onClick={onOpenSheet}
            className="flex shrink-0 items-center gap-1.5 rounded-[10px] px-3 py-2 text-[13px] font-bold"
            style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
          >
            <Car className="h-[18px] w-[18px]" /> Load it
          </button>
        </div>
      )}

      <DispatchLoadSheet
        open={sheetOpen}
        onClose={onCloseSheet}
        summary={summary}
        vehicles={vehicles}
        drivers={drivers}
        submitting={submitting}
        onSubmit={onBook}
        onAddVehicle={onAddVehicle}
        onAddDriver={onAddDriver}
      />
    </PhonePage>
  );
}
